import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Video, Award, Users, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT = {
  hero_headline: "Pass Your Certification. Start Your Career Faster.",
  hero_subheadline: "Structured certification prep, study tools, and real-world guidance designed to help you succeed without feeling overwhelmed.",
  hero_btn_primary: "Start Preparing Now",
  hero_btn_secondary: "Login to Dashboard",
  why_title: "Why Students Choose PUSH Certification Prep",
  why_subtitle: "We make certification prep simple, structured, and focused on real results — not confusion.",
  feature_1_title: "Step-by-Step Video Lessons", feature_1_desc: "Clear, easy-to-follow instruction that breaks down complex skills so you actually understand them.",
  feature_2_title: "Certification-Focused Study Tools", feature_2_desc: "Practice materials, guides, and quizzes designed to help you retain information and pass your exam.",
  feature_3_title: "Optional Live Support", feature_3_desc: "Get additional guidance when you need it so you never feel stuck or unsure.",
  feature_4_title: "Flexible Learning on Your Schedule", feature_4_desc: "Study when it works for you without pressure or rigid timelines.",
  programs_title: "Choose Your Certification Prep Path",
  programs_subtitle: "Allied Health certification prep designed for real results",
  cta_headline: "Stop Guessing. Start Passing.",
  cta_description: "Join students who are preparing smarter, not harder, with structured certification prep designed for real results.",
  cta_button: "Explore Prep Options"
};

export const PrepLanding = () => {
  const [programs, setPrograms] = useState([]);
  const [c, setC] = useState(DEFAULT);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/prep/programs`),
      axios.get(`${API}/prep/content/prep-home`)
    ]).then(([p, ct]) => {
      setPrograms(p.data);
      setC({ ...DEFAULT, ...ct.data });
    }).catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">

      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">{c.hero_headline}</h1>
          <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">{c.hero_subheadline}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prep/programs">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 text-lg px-8 py-6">
                {c.hero_btn_primary} <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/prep/login">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-blue-800 text-lg px-8 py-6">
                {c.hero_btn_secondary}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{c.why_title}</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">{c.why_subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[{icon:Video,n:1},{icon:BookOpen,n:2},{icon:Users,n:3},{icon:Clock,n:4}].map(({icon:Icon,n}) => (
              <Card key={n} className="border-2 hover:border-blue-200 transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-blue-600" size={24} />
                  </div>
                  <CardTitle className="text-xl">{c[`feature_${n}_title`]}</CardTitle>
                </CardHeader>
                <CardContent><CardDescription className="text-base">{c[`feature_${n}_desc`]}</CardDescription></CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{c.programs_title}</h2>
            <p className="text-lg text-gray-600">{c.programs_subtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program) => (
              <Card key={program.id} className="border-2 hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <CardTitle className="text-2xl">{program.title}</CardTitle>
                    <Award className="text-blue-600" size={32} />
                  </div>
                  <CardDescription className="text-base">{program.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between"><span className="text-gray-600">Duration:</span><span className="font-semibold">{program.duration}</span></div>
                    <div className="flex justify-between"><span className="text-gray-600">Students:</span><span className="font-semibold">{program.enrollment_count}+ enrolled</span></div>
                    <div className="border-t pt-4">
                      <ul className="space-y-2 mb-4">
                        {program.features.slice(0,3).map((f,i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                            <span className="text-gray-600">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t">
                      <span className="text-3xl font-bold text-blue-600">${program.price}</span>
                      <Link to="/prep/programs"><Button className="bg-blue-600 hover:bg-blue-700 text-white">Learn More</Button></Link>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{c.cta_headline}</h2>
          <p className="text-xl text-blue-100 mb-8">{c.cta_description}</p>
          <Link to="/prep/programs">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6">
              {c.cta_button} <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-6 bg-blue-950">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-blue-300 text-sm">
            PUSH Training - NC provides certification preparation and educational support. We do not issue certifications. Certification is obtained through accredited testing organizations.
          </p>
        </div>
      </section>
    </div>
  );
};
